import React, { useState, useEffect, useCallback, useMemo } from 'react'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Button from '@react/shared/ui/Button'
import RunList  from './components/RunList'
import RunPanel from './components/RunPanel'
import ProductionView from './components/ProductionView'
import IssueModal from './components/IssueModal'
import { fetchIssues, fetchPromoted, checkPoint, setTestsStatus } from './api'
import { isValidated, matchesSearch, REACH_GITHUB, RETRY_CHECK } from './helpers'

const FILTERS = [
  { key: 'todo',  label: 'À tester',   test: issue => !issue.technical && !isValidated(issue) && issue.tests_state !== 'tested', empty: 'Toutes les demandes sont testées.' },
  { key: 'tofix', label: 'À corriger', test: issue => !issue.technical && issue.tests_state === 'tested',                         empty: 'Les demandes à corriger s’affichent ici.' },
  { key: 'done',  label: 'Validées',   test: issue => !issue.technical && isValidated(issue),                                     empty: 'Les demandes validées s’affichent ici.' },
  { key: 'all',   label: 'Toutes',     test: () => true,                                                                          empty: 'Les demandes à tester s’affichent ici.' }
]

const VIEWS = [
  { key: 'tests',      label: 'Tests',         icon: 'list-check' },
  { key: 'production', label: 'En production', icon: 'rocket' }
]

// The open issue (#11348) or view (#production) lives in the URL, so a link to it can be shared.
const hashNumber = () => Number(window.location.hash.replace('#', '')) || null
const hashView   = () => (window.location.hash === '#production' ? 'production' : 'tests')

const promotedText = (issue) => [
  issue.title,
  ...issue.tickets.map(ticket => [ticket.account, `#${ticket.id}`, ticket.title].filter(Boolean).join(' '))
].join(' ')

const App = ({ issues_path, promoted_path, issue_path, check_path, status_path, comment_path, files_path, current_name }) => {
  const [issues,   setIssues]   = useState(null)
  const [loading,  setLoading]  = useState(false)
  const [notice,   setNotice]   = useState(null)
  const [filter,   setFilter]   = useState('todo')
  const [search,   setSearch]   = useState('')
  const [saving,   setSaving]   = useState({})
  const [selected, setSelected] = useState(hashNumber)
  const [refreshKey, setRefreshKey] = useState(0)
  const [modalNumber, setModalNumber] = useState(null)
  const [testsStates, setTestsStates] = useState([])
  const [savingStatus, setSavingStatus] = useState({})
  const [view,     setView]     = useState(hashView)
  const [promoted, setPromoted] = useState(null)
  const [loadingPromoted, setLoadingPromoted] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    fetchIssues(issues_path)
      .then(data => {
        if (data && data.error) return setNotice(data.error)
        setIssues(data.issues || [])
        setTestsStates(data.tests_states || [])
      })
      .catch(() => setNotice(REACH_GITHUB))
      .finally(() => setLoading(false))
  }, [issues_path])

  useEffect(() => { load() }, [load])

  const loadPromoted = useCallback(() => {
    setLoadingPromoted(true)
    fetchPromoted(promoted_path)
      .then(data => (data && data.error ? setNotice(data.error) : setPromoted(data.issues || [])))
      .catch(() => setNotice(REACH_GITHUB))
      .finally(() => setLoadingPromoted(false))
  }, [promoted_path])

  useEffect(() => { loadPromoted() }, [loadPromoted])

  const switchView = (key) => {
    setView(key)
    window.history.replaceState(null, '', key === 'production' ? '#production' : (selected ? `#${selected}` : '#'))
  }

  // Not inside load: the stale-point reload keeps its notice and the open draft.
  const refresh = () => {
    setNotice(null)
    setRefreshKey(key => key + 1)
    load()
    loadPromoted()
  }

  const select = (number) => {
    setSelected(number)
    window.history.replaceState(null, '', `#${number}`)
  }

  const patchIssue = (number, changes) => setIssues(current => current && current.map(issue => (
    issue.number === number ? { ...issue, ...changes } : issue
  )))

  // Unticking a box takes the card off PASS on the board, so the page does too.
  const toggle = useCallback((issue, point, checked) => {
    const key      = `${issue.number}:${point.index}`
    const previous = { points: issue.points }
    patchIssue(issue.number, {
      points: previous.points.map(p => (
        p.index === point.index ? { ...p, checked, checked_by: checked ? current_name : null } : p
      ))
    })
    setSaving(current => ({ ...current, [key]: true }))
    setNotice(null)

    checkPoint(check_path, { number: issue.number, point, checked })
      .then(data => {
        if (data && data.points) return patchIssue(issue.number, { points: data.points })
        patchIssue(issue.number, previous)
        setNotice(data?.error || RETRY_CHECK)
        if (data?.stale) load()
      })
      .catch(() => { patchIssue(issue.number, previous); setNotice(RETRY_CHECK) })
      .finally(() => setSaving(current => ({ ...current, [key]: false })))
  }, [check_path, current_name, load])

  const changeStatus = useCallback((issue, state) => {
    const previous = { tests_state: issue.tests_state, tests_passed: issue.tests_passed }
    patchIssue(issue.number, { tests_state: state, tests_passed: state === 'pass' })
    setSavingStatus(current => ({ ...current, [issue.number]: true }))
    setNotice(null)

    setTestsStatus(status_path, { number: issue.number, state })
      .then(data => {
        if (data && data.tests_state) return
        patchIssue(issue.number, previous)
        setNotice(data?.error || 'Cliquez de nouveau sur le statut pour l’enregistrer sur GitHub.')
      })
      .catch(() => { patchIssue(issue.number, previous); setNotice('Cliquez de nouveau sur le statut pour l’enregistrer sur GitHub.') })
      .finally(() => setSavingStatus(current => ({ ...current, [issue.number]: false })))
  }, [status_path])

  const savingFor = (number) => Object.fromEntries(
    Object.entries(saving)
      .filter(([key, busy]) => busy && key.startsWith(`${number}:`))
      .map(([key]) => [Number(key.split(':')[1]), true])
  )

  const counts = useMemo(() => Object.fromEntries(
    FILTERS.map(f => [f.key, (issues || []).filter(f.test).length])
  ), [issues])

  const activeFilter = FILTERS.find(f => f.key === filter)

  const visible = useMemo(
    () => (issues || []).filter(issue => activeFilter.test(issue) && matchesSearch(issue, search)),
    [issues, activeFilter, search]
  )

  const visiblePromoted = useMemo(() => {
    const needle = search.trim()
    return promoted && promoted.filter(issue => matchesSearch({ number: issue.number, title: promotedText(issue) }, needle))
  }, [promoted, search])

  // A run ticked to 100% stays open even though it leaves the "À tester" list.
  const current = (issues || []).find(issue => issue.number === selected) || visible[0]

  const bumpComments = (number) => setIssues(list => list && list.map(issue => (
    issue.number === number ? { ...issue, comments_count: issue.comments_count + 1 } : issue
  )))

  return (
    <>
      <div className="shrink-0 border-b border-slate-200 bg-white px-6 py-[12px]">
        <div className="flex flex-wrap items-center gap-2">
          <div className="mr-auto flex flex-wrap items-center gap-3">
            <h1 className="m-0 text-[18px] font-bold tracking-tight text-slate-900">Tests CSM</h1>
            <nav className="flex items-center gap-1">
              {VIEWS.map(v => {
                const count = v.key === 'tests'
                  ? (issues && counts.todo)
                  : (promoted && promoted.filter(issue => issue.to_announce).length)
                return (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => switchView(v.key)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-lg border-0 px-2.5 py-1 font-sans text-[12.5px] font-medium transition focus:outline-none',
                      view === v.key ? 'bg-brand-50 text-brand-700' : 'bg-white/0 text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                    )}
                  >
                    <FontAwesomeIcon icon={v.icon} className="text-[11px]" />
                    {v.label}
                    {count > 0 && <span className="tabular-nums text-[11px] opacity-70">{count}</span>}
                  </button>
                )
              })}
            </nav>
          </div>

          {view === 'tests' && (
            <div className="inline-flex items-center gap-1 rounded-lg bg-slate-100 p-1" role="tablist">
              {FILTERS.map(f => (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.key}
                  onClick={() => setFilter(f.key)}
                  className={clsx(
                    'inline-flex h-7 items-center gap-1.5 rounded-md border-0 px-[12px] font-sans text-[12.5px] font-medium transition focus:outline-none',
                    filter === f.key ? 'bg-white text-slate-900 shadow-sm' : 'bg-transparent text-slate-500 hover:text-slate-700'
                )}
              >
                {f.label}
                <span className="text-[11px] tabular-nums text-slate-400">{counts[f.key]}</span>
              </button>
            ))}
          </div>
          )}

          <div className="relative w-full sm:w-64">
            <FontAwesomeIcon icon="magnifying-glass" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par titre ou numéro"
              className="h-9 w-full rounded-lg border-[1px] border-slate-200 bg-white pl-8 pr-[12px] font-sans text-[13px] text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-[3px] focus:ring-brand-500/20"
            />
          </div>

          <Button variant="secondary" size="md" onClick={refresh} disabled={loading || loadingPromoted}>
            <FontAwesomeIcon icon="rotate" spin={loading || loadingPromoted} className="text-[11px]" />
            Actualiser
          </Button>
        </div>
      </div>

      <div className="flex flex-1 flex-col bg-slate-200/70 px-6 py-[12px] lg:min-h-0">
        {notice && (
          <div className="mb-[12px] flex shrink-0 items-start gap-2 rounded-xl border-[1px] border-brand-200 bg-brand-50 px-[16px] py-[12px] text-[13px] text-brand-800">
            <FontAwesomeIcon icon="circle-info" className="mt-0.5 text-brand-500" />
            <span className="flex-1">{notice}</span>
            <button type="button" onClick={() => setNotice(null)} className="border-0 bg-transparent p-0 text-brand-500 hover:text-brand-700" aria-label="Fermer">
              <FontAwesomeIcon icon="xmark" />
            </button>
          </div>
        )}

        {view === 'production'
          ? <ProductionView issues={visiblePromoted} loading={loadingPromoted} searching={search.trim() !== ''} onOpenIssue={setModalNumber} />
          : (
          <div className="grid flex-1 gap-3 lg:min-h-0 lg:grid-cols-[minmax(0,min(420px,36%))_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)]">
            <div className="flex min-w-0 flex-col lg:min-h-0">
              <div className="flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-1">
                {loading && !issues && (
                  <div className="flex justify-center py-12 text-slate-400"><FontAwesomeIcon icon="circle-notch" spin size="2x" /></div>
                )}
                {issues && visible.length === 0 && (
                  <div className="rounded-2xl bg-white px-[20px] py-12 text-center text-[13.5px] text-slate-500">
                    {search.trim() !== ''
                      ? 'Modifiez la recherche pour afficher d’autres demandes.'
                      : counts.all === 0 ? 'Les demandes à tester s’affichent ici.' : activeFilter.empty}
                  </div>
                )}
                {issues && <RunList issues={visible} selected={current?.number} onSelect={select} />}
              </div>
            </div>

            <div className="min-w-0 overflow-hidden rounded-2xl border-[1px] border-slate-300 bg-white lg:h-full lg:min-h-0">
              <div className="h-full lg:overflow-y-auto">
                {current
                  ? (
                    <RunPanel
                      key={current.number}
                      issue={current}
                      issuePath={issue_path}
                      commentPath={comment_path}
                      filesPath={files_path}
                      currentName={current_name}
                      refreshKey={refreshKey}
                      saving={savingFor(current.number)}
                      onToggle={toggle}
                      onCommented={bumpComments}
                      onOpenIssue={setModalNumber}
                      testsStates={testsStates}
                      savingStatus={!!savingStatus[current.number]}
                      onChangeStatus={changeStatus}
                    />
                    )
                  : issues && (
                    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-20 text-center">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                        <FontAwesomeIcon icon="clipboard-check" className="text-[18px] text-slate-400" />
                      </span>
                      <p className="m-0 text-[13.5px] font-medium text-slate-700">Le détail de la demande s’affiche ici.</p>
                    </div>
                    )}
              </div>
            </div>
          </div>
            )}
      </div>

      <IssueModal number={modalNumber} issuePath={issue_path} onClose={() => setModalNumber(null)} />
    </>
  )
}

export default App
